import { useCallback, useEffect, useMemo } from 'react';
import { LaunchBulkEditCommission } from 'src/components/common/BulkActions/BulkEditCommissionAction';
import { useUpdatePurchasesPermissions } from 'src/components/Purchases/BulkActions/useUpdatePurchasesPermissions';
import {
  IPosEntity,
  useActivePosEntityContext,
} from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { BulkEditCommissionDialog } from 'src/dialogs/BulkEdits/BulkEditCommissionDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BulkEditListingClient,
  BulkEditPreviewWithDetails,
  CommandOptionType,
  Commission,
  Event,
  ListingQuery,
} from 'src/WebApiController';

import { INVENTORY_BULK_UPDATE_COMMISSION_UPDATE_KEY } from '../../InventoryActionDropdown.constants';
import { BulkActionItem } from '../types';

export const useBulkEditCommissionAction = (
  filterQueryWithEventIds: ListingQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const { hasPurchaseUpdateCommissionPermission } =
    useUpdatePurchasesPermissions();

  const { activeAccountWebClientConfig } = useAppContext();
  const { setActivePosEntity } = useActivePosEntityContext<IPosEntity>();
  const updateKey = event
    ? `listingCommission-event-${event.virtId}`
    : INVENTORY_BULK_UPDATE_COMMISSION_UPDATE_KEY;
  const {
    eventsExpansion: { setListItemExpansion, refreshExpandedListItems },
  } = useCatalogDataContext();
  const { showErrorDialog } = useClientLogContext();

  const {
    openDialog: openBulkEditCommissionDialog,
    closeDialog: closeBulkEditCommissionDialog,
  } = useDialog(DialogId.BulkEditCommission, BulkEditCommissionDialog);

  const onSubmit = useCallback(
    async (
      commissions: Commission[] | null,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const preview = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkUpdatePurchaseCommissionsPreview(filterQueryWithEventIds);

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkUpdatePurchaseCommissionsPreview', error, {
              trackErrorData: { filterQueryWithEventIds },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else {
        await tryInvokeApi(
          async () => {
            const succeeded = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkUpdatePurchaseCommissions(
              {
                item1: preview!.preview,
                item2: commissions ?? [],
              },
              updateKey,
              supportBackgroundProcess
            );

            if (!supportBackgroundProcess) {
              if (succeeded) {
                // Invalidate the activePosEntity, so that when clicking on listingDetails
                // we force re-loading the listing and newest pricing settings can be displayed
                setActivePosEntity(0);
                await refreshExpandedListItems();
              }

              closeBulkEditCommissionDialog();
            }
          },
          (error) => {
            showErrorDialog('onSubmitEditCommission', error, {
              trackErrorData: { commissions, preview },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      setIsLoading,
      activeAccountWebClientConfig,
      filterQueryWithEventIds,
      showErrorDialog,
      updateKey,
      closeBulkEditCommissionDialog,
      setActivePosEntity,
      refreshExpandedListItems,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    if (event) {
      setListItemExpansion(true, [event.virtId]);
    }
    openBulkEditCommissionDialog({
      updateKey,
      onOkay: onSubmit,
      onClosed: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkEditCommissionDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkEditCommissionAction,
        onSelect: openDialogStableRef,
      },
      disabled: !hasPurchaseUpdateCommissionPermission,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkEditCommissionAction,
        disabled: !hasPurchaseUpdateCommissionPermission,
      });
    };
  }, [
    hasPurchaseUpdateCommissionPermission,
    openDialogStableRef,
    registerCommand,
    unregisterCommand,
  ]);

  const onLaunchAction = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (event) {
        setListItemExpansion(true, [event.virtId]);
      }
      openBulkEditCommissionDialog({
        updateKey,
        onOkay: onSubmit,
        onClosed: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkEditCommissionDialog,
      });
    },
    [
      closeBulkEditCommissionDialog,
      event,
      onSubmit,
      openBulkEditCommissionDialog,
      setIsLoading,
      setListItemExpansion,
      updateKey,
    ]
  );

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasPurchaseUpdateCommissionPermission) {
      return [];
    }
    return [
      {
        title: ContentId.UpdateBuyerCommission,
        disabled: isLoading,
        onClick: onLaunchAction,
      },
    ];
  }, [hasPurchaseUpdateCommissionPermission, isLoading, onLaunchAction]);

  return {
    dropDown: (
      <LaunchBulkEditCommission
        key="EditCommission"
        onClick={onLaunchAction}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
